import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useTranslation } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { useEffect, useState } from 'react';
import { ForgotPasswordService } from '../services';
import { ForgotPasswordError } from '../types';

export interface UseForgotPasswordReturn {
    sendResetLink: (email: string) => Promise<void>;
    verifyOTP: (otp: string) => Promise<{ success: boolean; oid?: string }>; // Returns success status and OID
    resetPassword: (newPassword: string, confirmPassword: string) => Promise<boolean>;
    setForgotPassword: (email: string, otp: string, transactionId: string, oid: string, password: string) => Promise<boolean>;
    loading: boolean;
    verifyingOTP: boolean;
    resettingPassword: boolean;
    error: string | null;
    success: boolean;
    transactionId: string | null;
    oid: string | null; // OID from verify-forgot-otp response
    email: string | null; // Email used for forgot password
    otp: string | null; // OTP used for verification
    clearError: () => void;
    clearSuccess: () => void;
}

/**
 * Custom hook for handling forgot password functionality
 */
export const useForgotPassword = (): UseForgotPasswordReturn => {
    const { t, currentLanguage } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [verifyingOTP, setVerifyingOTP] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorKey, setErrorKey] = useState<string | null>(null); // Store translation key instead of translated message
    const [success, setSuccess] = useState(false);
    const [transactionId, setTransactionId] = useState<string | null>(null);
    const [oid, setOid] = useState<string | null>(null); // OID from verify-forgot-otp response
    const [email, setEmail] = useState<string | null>(null); // Email used for forgot password
    const [otp, setOtp] = useState<string | null>(null); // OTP used for verification

    // Re-translate error message when language changes
    useEffect(() => {
        if (errorKey) {
            setError(t(errorKey as any));
        }
    }, [currentLanguage, errorKey, t]);

    const sendResetLink = async (emailInput: string): Promise<void> => {
        setLoading(true);
        setError(null);
        setErrorKey(null);
        setSuccess(false);
        setTransactionId(null);
        setOid(null);
        setEmail(emailInput);
        setOtp(null);

        try {
            const response = await ForgotPasswordService.sendResetLink({ email: emailInput });

            if (response.status === 'success' && response.details?.transactionId) {
                trackCatalogEvent({
                    screen: DT_SCREEN.FORGOT_PASSWORD,
                    actionName: 'forgot_password_send_link',
                    eventType: 'action',
                });
                setSuccess(true);
                setTransactionId(response.details.transactionId);
            } else {
                // Treat missing transactionId as an error
                throw {
                    code: 'INVALID_RESPONSE',
                    message: response.details?.message || 'Failed to send OTP',
                } as ForgotPasswordError;
            }
        } catch (err) {
            const forgotPasswordError = err as ForgotPasswordError;
            trackCatalogEvent({
                screen: DT_SCREEN.FORGOT_PASSWORD,
                actionName: 'forgot_password_send_link_error',
                eventType: 'error',
                properties: {
                    p_failedMessage: forgotPasswordError.message || 'Failed to send OTP',
                },
            });
            setError(forgotPasswordError.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const verifyOTP = async (otp: string): Promise<{ success: boolean; oid?: string }> => {
        if (!transactionId) {
            const errorMsg = 'Transaction ID is missing. Please request OTP again.';
            setError(errorMsg);
            logger.error('ForgotPassword', 'Verify OTP failed - no transactionId');
            return { success: false };
        }

        if (!email) {
            const errorMsg = 'Email is missing. Please request OTP again.';
            setError(errorMsg);
            logger.error('ForgotPassword', 'Verify OTP failed - no email');
            return { success: false };
        }

        setVerifyingOTP(true);
        setError(null);
        setErrorKey(null);

        logger.info('ForgotPassword', 'Verifying OTP', { email, otp, transactionId });

        try {
            const response = await ForgotPasswordService.verifyOTP({
                email,
                otp,
                transactionId,
            });

            logger.info('ForgotPassword', 'Verify OTP response', { response });

            // Check for error in response (wrong transactionId)
            if (response.error) {
                setError(response.error);
                logger.warn('ForgotPassword', 'Verify OTP failed - error in response', { error: response.error });
                return { success: false };
            }

            const serverMessage = response.details?.message || '';
            const messageLower = serverMessage.toLowerCase();
            
            // Check if verification was successful
            const isSuccessful = messageLower.includes('successfully') && 
                                !messageLower.includes('unsuccessfully');
            
            // Check if message indicates expired/timeout
            const isExpired = messageLower.includes('expired') || 
                            messageLower.includes('timeout');
            
            // Check if message indicates unsuccessful/wrong OTP (but not expired)
            const isUnsuccessful = (messageLower.includes('unsuccessfully') || 
                                   messageLower.includes('wrong otp') ||
                                   (messageLower.includes('wrong') && messageLower.includes('otp'))) && 
                                   !isExpired;

            // If message doesn't match our known patterns (successful, unsuccessful, expired), show server message
            if (!isSuccessful && !isUnsuccessful && !isExpired && serverMessage) {
                setError(serverMessage);
                setErrorKey(null);
                logger.warn('ForgotPassword', 'Verify OTP - unknown message pattern, showing server message', { 
                    message: serverMessage 
                });
                return { success: false };
            }

            // Check for unsuccessful verification (wrong OTP but correct transactionId)
            if (isUnsuccessful) {
                // Use generic error message for unsuccessful (but not expired)
                setErrorKey('otp.invalidOTPPleaseTryAgain');
                setError(t('otp.invalidOTPPleaseTryAgain'));
                logger.warn('ForgotPassword', 'Verify OTP failed - invalid OTP', { response });
                return { success: false };
            }
            
            // Check for expired/timeout messages
            if (isExpired && !isSuccessful) {
                // Use translation for expired/timeout messages
                setErrorKey('otp.otpExpired' as any);
                setError(t('otp.otpExpired' as any));
                logger.warn('ForgotPassword', 'Verify OTP failed - OTP expired/timeout', { 
                    message: serverMessage 
                });
                return { success: false };
            }

            // Success case
            if (response.status === 'success' && response.details?.message?.includes('successfully')) {
                // Capture OID from response
                const responseOid = response.details?.oid;
                if (responseOid) {
                    setOid(responseOid);
                    setOtp(otp); // Store OTP for later use
                    logger.info('ForgotPassword', 'OTP verified successfully, OID captured', { 
                        oid: responseOid,
                        hasOid: !!responseOid 
                    });
                    return { success: true, oid: responseOid };
                } else {
                    logger.warn('ForgotPassword', 'OTP verified but OID not found in response', { response });
                    setOtp(otp); // Store OTP for later use
                    return { success: true };
                }
            }

            // Fallback: if status is success but message is unclear, assume success
            if (response.status === 'success') {
                // Capture OID from response even in fallback case
                const responseOid = response.details?.oid;
                if (responseOid) {
                    setOid(responseOid);
                    logger.info('ForgotPassword', 'OTP verified (fallback success), OID captured', { 
                        oid: responseOid 
                    });
                    setOtp(otp); // Store OTP for later use
                    return { success: true, oid: responseOid };
                } else {
                    setOtp(otp); // Store OTP for later use
                    logger.info('ForgotPassword', 'OTP verified (fallback success)', { response });
                    return { success: true };
                }
            }

            const errorMsg = 'Failed to verify OTP';
            setError(errorMsg);
            logger.warn('ForgotPassword', 'Verify OTP failed - unknown status', { response });
            return { success: false };
        } catch (err) {
            const forgotPasswordError = err as ForgotPasswordError;
            const errorMsg = forgotPasswordError.message || 'Failed to verify OTP';
            setError(errorMsg);
            logger.error('ForgotPassword', 'Verify OTP exception', { error: forgotPasswordError });
            return { success: false };
        } finally {
            setVerifyingOTP(false);
        }
    };

    const resetPassword = async (newPassword: string, confirmPassword: string): Promise<boolean> => {
        if (!transactionId) {
            const errorMsg = 'Transaction ID is missing. Please start the password reset process again.';
            setError(errorMsg);
            logger.error('ForgotPassword', 'Reset password failed - no transactionId');
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError('New password and confirm password do not match');
            logger.error('ForgotPassword', 'Password mismatch');
            return false;
        }

        setResettingPassword(true);
        setError(null);
        setErrorKey(null);

        logger.info('ForgotPassword', 'Resetting password', { transactionId });

        try {
            const response = await ForgotPasswordService.resetPassword({
                transactionId,
                new_password: newPassword,
                new_password_confirm: confirmPassword,
            });

            logger.info('ForgotPassword', 'Password reset successfully', { message: response.message });
            return true;
        } catch (err) {
            const forgotPasswordError = err as ForgotPasswordError;
            const errorMsg = forgotPasswordError.message || 'Failed to reset password';
            setError(errorMsg);
            logger.error('ForgotPassword', 'Reset password failed', { error: forgotPasswordError });
            return false;
        } finally {
            setResettingPassword(false);
        }
    };

    const clearError = () => {
        setError(null);
        setErrorKey(null);
    };
    const setForgotPassword = async (emailInput: string, otpInput: string, transactionIdInput: string, oidInput: string, password: string): Promise<boolean> => {
        if (!transactionIdInput || !oidInput) {
            const errorMsg = 'Transaction ID or OID is missing. Please start the password reset process again.';
            setError(errorMsg);
            logger.error('ForgotPassword', 'Set forgot password failed - missing transactionId or oid', {
                hasTransactionId: !!transactionIdInput,
                hasOid: !!oidInput,
            });
            return false;
        }

        setResettingPassword(true);
        setError(null);
        setErrorKey(null);

        logger.info('ForgotPassword', 'Setting forgot password', {
            email: emailInput,
            hasOtp: !!otpInput,
            hasPassword: !!password,
            transactionId: transactionIdInput,
            oid: oidInput,
        });

        try {
            const response = await ForgotPasswordService.setForgotPassword({
                email: emailInput,
                otp: otpInput,
                transactionId: transactionIdInput,
                oid: oidInput,
                password,
            });

            if (response.status === 'success') {
                logger.info('ForgotPassword', 'Password set successfully', { message: response.details?.message });
                return true;
            } else {
                const errorMsg = response.error || response.details?.message || 'Failed to set password';
                setError(errorMsg);
                logger.error('ForgotPassword', 'Set forgot password failed', { response });
                return false;
            }
        } catch (err: any) {
            const errorMsg = err?.response?.data?.error || err?.message || 'Failed to set password';
            setError(errorMsg);
            logger.error('ForgotPassword', 'Set forgot password exception', { error: err });
            return false;
        } finally {
            setResettingPassword(false);
        }
    };

    const clearSuccess = () => {
        setSuccess(false);
        setTransactionId(null);
        setOid(null);
        setOtp(null);
    };

    return {
        sendResetLink,
        verifyOTP,
        resetPassword,
        setForgotPassword,
        loading,
        verifyingOTP,
        resettingPassword,
        error,
        success,
        transactionId,
        oid,
        email,
        otp,
        clearError,
        clearSuccess,
    } as UseForgotPasswordReturn;
};